// Named sidebar data providers for the Calendar app.
//
// One provider, for the Calendar panel's "Today" list: its endpoint is workspace-scoped
// (`/{slug}/calendar/...`), each row's subtitle is a time range formatted from the
// record, and it must refetch on the remote's change signal — none of which a
// declarative `{ api, map }` can express.

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { apiService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import { usePermissionsSlice } from '@clarix/rbac';
import type { RowConfig, SidebarProviderResult } from '@clarix/shared-types';
import { CALENDAR_SHELL_PERM as P } from '../../calendar-permissions';
import { CALENDAR_SIGNAL } from '../../quick-meeting/constants';
import { useNow } from '../../quick-meeting/useNow';

/** The page's one-shot "open this event" intent (apps/calendar CALENDAR_INTENT.EVENT). */
const EVENT_INTENT = 'event';

/** The endpoint's ceiling (LIST_MAX), so the "N upcoming" count is the real one. */
const UPCOMING_LIMIT = 50;
/** Ended meetings drop off between fetches. */
const TICK_MS = 30_000;
const REFETCH_MS = 5 * 60_000;

/** The fields of `GET /events/upcoming`'s `Event` rows this list reads. */
interface UpcomingEventWire {
  id: string;
  title: string;
  start: string;
  end: string;
  mode: 'internal' | 'external';
}

interface PreferencesWire {
  time_format_24h?: boolean;
}

/**
 * The caller's Scheduled meetings later today, soonest first. A row opens that day in
 * day view with the meeting's drawer up.
 */
export function useCalendarAppUpcomingProvider(): SidebarProviderResult {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const { permissions, loadedApps } = usePermissionsSlice();
  // Providers run even when their section is hidden, so the gate is repeated here.
  const allowed = loadedApps.calendar === true && permissions[P.MEETINGS_VIEW] === true;
  const enabled = !!slug && allowed;

  const upcoming = useQuery({
    queryKey: ['calendar', slug, 'events', 'upcoming'] as const,
    queryFn: () =>
      apiService.get<UpcomingEventWire[]>(`/${slug}/calendar/events/upcoming`, {
        params: { limit: UPCOMING_LIMIT },
      }),
    enabled,
    staleTime: 30_000,
    refetchInterval: REFETCH_MS,
  });
  // The grid's clock format, so a row reads like the page it opens.
  const prefs = useQuery({
    queryKey: ['calendar', slug, 'preferences'] as const,
    queryFn: () => apiService.get<PreferencesWire>(`/${slug}/calendar/preferences`),
    enabled,
    staleTime: 60_000,
  });

  // The remote's RTK Query cache can't reach this one, so its window signal (shell-signals.ts) refetches.
  const { refetch } = upcoming;
  useEffect(() => {
    const onChanged = () => void refetch();
    window.addEventListener(CALENDAR_SIGNAL.EVENTS_CHANGED, onChanged);
    return () => window.removeEventListener(CALENDAR_SIGNAL.EVENTS_CHANGED, onChanged);
  }, [refetch]);

  const now = useNow(TICK_MS);
  const h24 = prefs.data?.time_format_24h === true;
  const rows = useMemo<RowConfig[]>(() => {
    const time = (iso: string) => format(new Date(iso), h24 ? 'HH:mm' : 'h:mm a');
    return (enabled ? (upcoming.data ?? []) : [])
      .filter((e) => Date.parse(e.end) > now)
      .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
      .map((e) => {
        const params = new URLSearchParams({
          view: 'day',
          date: format(new Date(e.start), 'yyyy-MM-dd'),
          [EVENT_INTENT]: String(e.id),
        });
        return {
          id: String(e.id),
          label: e.title,
          subtitle: `${time(e.start)} – ${time(e.end)}`,
          to: `/calendar?${params.toString()}`,
          trailingChevron: true,
          // An action row: it opens a meeting, it is never "where you are".
          neverActive: true,
        };
      });
  }, [enabled, upcoming.data, now, h24]);

  return {
    rows,
    isLoading: enabled && upcoming.isPending,
    isError: upcoming.isError,
    emptyText: 'No upcoming meetings today',
  };
}
